import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, Clock, MessageSquare } from 'lucide-react';
import Header from './components/Header25';
import Footer from './components/Footer25';

const Contact25 = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '', email: '', phone: '', subject: '', message: '',
    });
    const [submitted, setSubmitted] = useState(false);

    const handleInput = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
    };

    const inputStyle = {
        width: '100%', padding: '14px 16px',
        border: '1px solid #DEE2E6', background: '#FFFFFF',
        fontSize: 14, outline: 'none', color: '#111111',
        fontFamily: 'IBM Plex Sans, sans-serif',
        borderRadius: 0, transition: 'border-color 0.2s'
    };

    const labelStyle = {
        display: 'block', fontSize: 12, fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '1px',
        marginBottom: 8, fontFamily: 'Space Grotesk, sans-serif'
    };

    const contactMethods = [
        {
            icon: Phone, title: 'Phone / WhatsApp',
            detail: '+91 98765 43210',
            sub: 'Mon-Sat, 10AM - 7PM IST',
            action: 'tel:+919876543210'
        },
        {
            icon: Mail, title: 'Email Us',
            detail: 'hello@sahajfashion.com',
            sub: 'We reply within 24 hours',
            action: 'mailto:hello@sahajfashion.com'
        },
        {
            icon: MapPin, title: 'Visit Studio',
            detail: 'Bandra West, Mumbai',
            sub: 'Maharashtra 400050',
            action: '#'
        },
        {
            icon: Clock, title: 'Working Hours',
            detail: 'Mon - Sat: 10AM - 7PM',
            sub: 'Sunday: Closed',
            action: null
        },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap');
                .sahaj-contact * { box-sizing: border-box; }
                .sahaj-contact { font-family: 'IBM Plex Sans', sans-serif; background: #FFFFFF; color: #111111; }
                .sahaj-contact-heading { font-family: 'Space Grotesk', sans-serif; }
                .sahaj-contact input:focus, .sahaj-contact textarea:focus, .sahaj-contact select:focus { border-color: #0D7C66 !important; }
            `}</style>
            <div className="sahaj-contact" style={{ minHeight: '100vh' }}>
                <Header />

                {/* Hero */}
                <div style={{ background: '#111111', padding: '60px 0', marginTop: 80 }}>
                    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px' }}>
                        <h1 className="sahaj-contact-heading" style={{
                            fontSize: 48, fontWeight: 700, color: '#FFFFFF', margin: 0, letterSpacing: '-1px'
                        }}>
                            GET IN TOUCH
                        </h1>
                        <p style={{ fontSize: 16, color: '#9CA3AF', marginTop: 12, fontWeight: 300 }}>
                            We'd love to hear from you
                        </p>
                    </div>
                </div>

                <div style={{ maxWidth: 1280, margin: '0 auto', padding: '60px 24px 80px' }}>
                    {/* Contact Methods */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 60 }}>
                        {contactMethods.map((method, idx) => (
                            <a
                                key={idx}
                                href={method.action || '#'}
                                style={{
                                    padding: 28, border: '1px solid #DEE2E6', textDecoration: 'none',
                                    color: '#111111', transition: 'border-color 0.2s, box-shadow 0.2s',
                                    display: 'block'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = '#0D7C66'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(13,124,102,0.1)'; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = '#DEE2E6'; e.currentTarget.style.boxShadow = 'none'; }}
                            >
                                <div style={{
                                    width: 48, height: 48, background: '#E8F5F1', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', marginBottom: 16
                                }}>
                                    <method.icon size={22} color="#0D7C66" />
                                </div>
                                <h3 className="sahaj-contact-heading" style={{ fontSize: 16, fontWeight: 600, margin: '0 0 8px' }}>
                                    {method.title}
                                </h3>
                                <p style={{ fontSize: 15, fontWeight: 500, margin: '0 0 4px' }}>{method.detail}</p>
                                <p style={{ fontSize: 13, color: '#6C757D', margin: 0 }}>{method.sub}</p>
                            </a>
                        ))}
                    </div>

                    {/* WhatsApp CTA */}
                    <div style={{
                        background: '#0D7C66', padding: '32px 40px', marginBottom: 60,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        flexWrap: 'wrap', gap: 20
                    }}>
                        <div>
                            <h3 className="sahaj-contact-heading" style={{ fontSize: 24, fontWeight: 700, color: '#FFFFFF', margin: '0 0 8px' }}>
                                Quick Connect via WhatsApp
                            </h3>
                            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.8)', margin: 0 }}>
                                Get instant responses for orders, custom requests, and sizing help
                            </p>
                        </div>
                        <a
                            href="https://wa.me/919876543210"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                padding: '14px 32px', background: '#FFFFFF', color: '#0D7C66',
                                textDecoration: 'none', fontSize: 14, fontWeight: 700,
                                display: 'flex', alignItems: 'center', gap: 10,
                                textTransform: 'uppercase', letterSpacing: '1px',
                                fontFamily: 'Space Grotesk, sans-serif'
                            }}
                        >
                            <MessageSquare size={18} />
                            Chat on WhatsApp
                        </a>
                    </div>

                    {/* Form Section */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60 }}>
                        <div>
                            <h2 className="sahaj-contact-heading" style={{
                                fontSize: 32, fontWeight: 700, margin: '0 0 12px', letterSpacing: '-0.5px'
                            }}>
                                SEND US A MESSAGE
                            </h2>
                            <p style={{ fontSize: 15, color: '#6C757D', marginBottom: 40, lineHeight: 1.6 }}>
                                Have a question about sizing, custom orders, or anything else? Fill out the form and we'll get back to you promptly.
                            </p>

                            {submitted ? (
                                <div style={{ padding: 40, background: '#E8F5F1', textAlign: 'center' }}>
                                    <div style={{ width: 64, height: 64, background: '#0D7C66', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Send size={28} color="#FFFFFF" />
                                    </div>
                                    <h3 className="sahaj-contact-heading" style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Message Sent!</h3>
                                    <p style={{ color: '#6C757D' }}>We'll get back to you within 24 hours.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                                        <div>
                                            <label style={labelStyle}>Full Name</label>
                                            <input name="name" value={formData.name} onChange={handleInput} style={inputStyle} placeholder="Your name" required />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Email</label>
                                            <input name="email" type="email" value={formData.email} onChange={handleInput} style={inputStyle} placeholder="your@email.com" required />
                                        </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                                        <div>
                                            <label style={labelStyle}>Phone</label>
                                            <input name="phone" value={formData.phone} onChange={handleInput} style={inputStyle} placeholder="+91 XXXXX XXXXX" />
                                        </div>
                                        <div>
                                            <label style={labelStyle}>Subject</label>
                                            <select name="subject" value={formData.subject} onChange={handleInput} style={{ ...inputStyle, cursor: 'pointer' }}>
                                                <option value="">Select subject</option>
                                                <option value="order">Order Inquiry</option>
                                                <option value="custom">Custom Order</option>
                                                <option value="sizing">Sizing Help</option>
                                                <option value="return">Return/Exchange</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div style={{ marginBottom: 24 }}>
                                        <label style={labelStyle}>Message</label>
                                        <textarea
                                            name="message" value={formData.message} onChange={handleInput}
                                            rows={6} style={{ ...inputStyle, resize: 'vertical' }}
                                            placeholder="Tell us how we can help..." required
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        style={{
                                            padding: '16px 48px', background: '#0D7C66', color: '#FFFFFF',
                                            border: 'none', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', gap: 10,
                                            textTransform: 'uppercase', letterSpacing: '2px',
                                            fontFamily: 'Space Grotesk, sans-serif',
                                            transition: 'background 0.2s'
                                        }}
                                        onMouseEnter={e => e.target.style.background = '#0A9B7F'}
                                        onMouseLeave={e => e.target.style.background = '#0D7C66'}
                                    >
                                        <Send size={16} />
                                        Send Message
                                    </button>
                                </form>
                            )}
                        </div>

                        {/* Map Placeholder */}
                        <div style={{
                            background: '#F8F9FA', border: '1px solid #DEE2E6',
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            justifyContent: 'center', minHeight: 400
                        }}>
                            <MapPin size={40} color="#DEE2E6" style={{ marginBottom: 16 }} />
                            <p className="sahaj-contact-heading" style={{ fontSize: 18, fontWeight: 600, color: '#6C757D', marginBottom: 4 }}>
                                Studio Location
                            </p>
                            <p style={{ fontSize: 14, color: '#6C757D' }}>Bandra West, Mumbai, Maharashtra 400050</p>
                            <p style={{ fontSize: 13, color: '#0D7C66', fontWeight: 500, marginTop: 12, cursor: 'pointer' }}>
                                Open in Google Maps →
                            </p>
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default Contact25;
